import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sessionsQueryOptions } from "../queries/session-queries";
import { defaultAgentQueryOptions } from "../queries/agents-queries";
import { SessionTypeIcon } from "./SessionTypeIcon";
import { AgentsIcon } from "@/shared/icons";
import {
  spacesListQueryOptions,
  personalSpaceQueryOptions,
} from "@/modules/spaces/spaces-queries";
import {
  formatHistoryTime,
  formatRowTime,
  groupByDate,
  type HistoryBucket,
} from "../lib/format-history-time";
import type { SessionInfo } from "../types";

type Tab = "all" | "personal" | "shared";

interface Props {
  onOpenSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
}

/**
 * История задач — full-area view shown when user clicks «История» in the
 * sidebar. Two sections: stubbed «Требуют действия» (HITL queue, not wired
 * yet) and real «Недавние» (the sessions list that used to live in the
 * sidebar). Right column hosts a stub «Полученные документы» panel.
 *
 * Filters/sort buttons are visual stubs for now per the brief.
 */
export function HistoryView({ onOpenSession, onDeleteSession }: Props) {
  const { data: sessions = [] } = useQuery(sessionsQueryOptions());
  const { data: spaces = [] } = useQuery(spacesListQueryOptions());
  const { data: personalSpace } = useQuery(personalSpaceQueryOptions());
  const { data: defaultAgent } = useQuery(defaultAgentQueryOptions());
  const [tab, setTab] = useState<Tab>("all");
  const [onlyAwaiting, setOnlyAwaiting] = useState(false);
  const [spaceFilterId, setSpaceFilterId] = useState<string | null>(null);
  const [spaceMenuOpen, setSpaceMenuOpen] = useState(false);
  const spaceMenuRef = useRef<HTMLDivElement>(null);

  // «Приватное» = ровно личное пространство (по id из /spaces/personal).
  // Раньше использовали `deriveUiType === "personal"`, но это путало
  // только что созданное командное пространство с одним участником
  // (его ui-тип тоже выводится как "personal" из-за member_count=1)
  // с настоящим Личным — командные чаты протекали в таб «Приватные».
  const isPersonalSession = (s: SessionInfo) =>
    !s.space || (!!personalSpace && s.space.id === personalSpace.id);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (spaceFilterId && s.space?.id !== spaceFilterId) return false;
      if (tab === "personal" && !isPersonalSession(s)) return false;
      if (tab === "shared" && isPersonalSession(s)) return false;
      return true;
    });
    // isPersonalSession closes over personalSpace; including it in deps
    // refreshes the filter when /spaces/personal arrives or changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, tab, spaceFilterId, personalSpace]);

  // If the chosen space disappears from the list (e.g. user lost access),
  // silently fall back to "all" so the toolbar doesn't show a dangling label.
  useEffect(() => {
    if (spaceFilterId && !spaces.some((s) => s.id === spaceFilterId)) {
      setSpaceFilterId(null);
    }
  }, [spaces, spaceFilterId]);

  useEffect(() => {
    if (!spaceMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (spaceMenuRef.current?.contains(e.target as Node)) return;
      setSpaceMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSpaceMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [spaceMenuOpen]);

  const spaceFilterLabel = spaceFilterId
    ? spaces.find((s) => s.id === spaceFilterId)?.name ?? "Все пространства"
    : "Все пространства";

  const awaiting = useMemo(
    () => filtered.filter((s) => s.awaiting_user_input),
    [filtered],
  );

  const recentGroups = useMemo(
    () => groupByDate(filtered, (s) => s.updated_at),
    [filtered],
  );

  const renderItem = (s: SessionInfo, bucket: HistoryBucket | null) => {
    const agentName = s.agent?.label || s.agent?.name;
    const isDefaultAgent =
      !!defaultAgent && !!s.agent && s.agent.ref === defaultAgent.ref;
    const showAgent = !!agentName && !isDefaultAgent;
    // Personal sessions don't carry a "в Личное" suffix — only team spaces
    // are surfaced inline. Sessions with no `space` (legacy / personal)
    // are also hidden.
    const showSpace =
      !!s.space && (!personalSpace || s.space.id !== personalSpace.id);
    // No bucket → flat list (awaiting): keep the day prefix in the row.
    const timeLabel = bucket
      ? formatRowTime(s.updated_at, bucket)
      : formatHistoryTime(s.updated_at);
    return (
      <div
        key={s.id}
        className={`history-item${s.awaiting_user_input ? " history-item--awaiting" : ""}`}
        onClick={() => onOpenSession(s.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") onOpenSession(s.id);
        }}
      >
        <SessionTypeIcon
          kind={s.routine_id ? "routine" : "chat"}
          awaiting={s.awaiting_user_input}
          running={s.is_running}
          variant="badge"
          className="history-item-icon"
        />
        <div className="history-item-content">
          <div className="history-item-title-row">
            <span className="history-item-title">{s.title || "Новый чат"}</span>
            {showSpace && s.space && (
              <span className="history-item-space" title={`Пространство: ${s.space.name}`}>
                {s.space.name}
              </span>
            )}
          </div>
          {(timeLabel || showAgent) && (
            <div className="history-item-meta">
              {timeLabel && <span>{timeLabel}</span>}
              {showAgent && (
                <span className="history-item-meta-agent" title={`Агент: ${agentName}`}>
                  <AgentsIcon className="history-item-bot-icon" />
                  <span>{agentName}</span>
                </span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="history-item-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteSession(s.id);
          }}
          title="Удалить"
          aria-label="Удалить"
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="history-view">
      <h1 className="cmd-page-title">История задач</h1>
      <div className="history-body">
        <div className="history-main">
          <div className="history-toolbar">
          <div className="history-tabs">
            <button
              type="button"
              className={`history-tab${tab === "all" ? " active" : ""}`}
              onClick={() => setTab("all")}
            >
              Все
            </button>
            <button
              type="button"
              className={`history-tab${tab === "personal" ? " active" : ""}`}
              onClick={() => setTab("personal")}
            >
              Приватные
            </button>
            <button
              type="button"
              className={`history-tab${tab === "shared" ? " active" : ""}`}
              onClick={() => setTab("shared")}
            >
              Командные
            </button>
          </div>

          <div className="history-filters">
            <label className="history-filter-check">
              <input
                type="checkbox"
                checked={onlyAwaiting}
                onChange={(e) => setOnlyAwaiting(e.target.checked)}
              />
              <span>Требуют действий</span>
            </label>
            <div className="history-filter-popover-anchor" ref={spaceMenuRef}>
              <button
                type="button"
                className={`history-filter-select${spaceFilterId ? " is-applied" : ""}${spaceMenuOpen ? " is-open" : ""}`}
                onClick={() => setSpaceMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={spaceMenuOpen}
              >
                <span className="history-filter-select-label">{spaceFilterLabel}</span>
                <Chevron />
              </button>
              {spaceMenuOpen && (
                <div className="history-filter-popover" role="menu">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={!spaceFilterId}
                    className={`history-filter-popover-item${!spaceFilterId ? " is-active" : ""}`}
                    onClick={() => {
                      setSpaceFilterId(null);
                      setSpaceMenuOpen(false);
                    }}
                  >
                    Все пространства
                  </button>
                  {spaces.length > 0 && <div className="history-filter-popover-sep" />}
                  {spaces.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={spaceFilterId === s.id}
                      className={`history-filter-popover-item${spaceFilterId === s.id ? " is-active" : ""}`}
                      onClick={() => {
                        setSpaceFilterId(s.id);
                        setSpaceMenuOpen(false);
                      }}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="history-section">
          <h2 className="cmd-page-section-title">Требуют действия</h2>
          {awaiting.length === 0 ? (
            <div className="history-empty">
              Здесь появятся задачи, в которых агент ждёт вашего ответа.
            </div>
          ) : (
            <div className="history-list">
              {awaiting.map((s) => renderItem(s, null))}
            </div>
          )}
        </section>

        {!onlyAwaiting && (
          <section className="history-section">
            <h2 className="cmd-page-section-title">Недавние</h2>
            {filtered.length === 0 ? (
              <div className="history-empty">Завершённых задач пока нет.</div>
            ) : (
              <div className="history-groups">
                {recentGroups.map((g) => (
                  <div key={g.key} className="history-group">
                    <StickyGroupHeader label={g.label} />
                    <div className="history-list">
                      {g.items.map((s) => renderItem(s, g.bucket))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

        <aside className="history-side">
          <div className="history-side-card">
            <div className="history-side-header">
              <span>Полученные документы</span>
              <Chevron />
            </div>
            <div className="history-side-empty">
              Здесь появятся документы из ваших задач.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/**
 * Sticky-state-aware date header. A 1px absolute sentinel at the top of
 * `.history-group` rides the natural flow; once it scrolls above the
 * scroll port (`.history-view`), the header is "stuck" and gets the
 * `is-stuck` class — at which point CSS extends the background upward
 * to cover the inter-group gap so items don't peek above the header.
 */
function StickyGroupHeader({ label }: { label: string }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    let root: Element | null = sentinel.parentElement;
    while (root) {
      const overflow = getComputedStyle(root).overflowY;
      if (overflow === "auto" || overflow === "scroll") break;
      root = root.parentElement;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setStuck(!entry.isIntersecting);
      },
      { root, threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="history-group-sentinel" aria-hidden="true" />
      <div className={`history-group-header${stuck ? " is-stuck" : ""}`}>{label}</div>
    </>
  );
}

function Chevron() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

